// Export tabellare condiviso degli artefatti PA (CSV / XLSX).
// Pattern repo: CSV con separatore `;` + BOM UTF-8 (vedi export POS/calendar).
// Memoria zNumeric: pg serializza numeric come STRINGA — le colonne `number`
// coerciscono le stringhe numeriche a numero in export, mai `toFixed` diretto.

export interface ExportColumn {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'boolean' | 'date' | string;
}

export type ExportRow = Record<string, unknown>;

export type ExportCell = string | number | boolean | null;

const NUMERIC_STRING_RE = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;

/** Coercizione tipata di una cella per l'export (CSV testo / XLSX nativo). */
export function coerceCell(value: unknown, type?: string): ExportCell {
  if (value === null || value === undefined) return null;
  if (type === 'number') {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && NUMERIC_STRING_RE.test(value.trim())) {
      const n = Number(value);
      return Number.isFinite(n) ? n : value;
    }
    return typeof value === 'string' ? value : String(value);
  }
  if (type === 'boolean') {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  return JSON.stringify(value);
}

/**
 * Comparatore type-aware per l'ordinamento client-side delle tabelle.
 * Gestisce numeric-as-string (pg), date ISO, booleani; null/undefined in coda.
 */
export function compareCells(a: unknown, b: unknown, type?: string): number {
  const aEmpty = a === null || a === undefined || a === '';
  const bEmpty = b === null || b === undefined || b === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (type === 'number') {
    const an = typeof a === 'number' ? a : Number(String(a));
    const bn = typeof b === 'number' ? b : Number(String(b));
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  }
  if (type === 'date') {
    const at = Date.parse(String(a));
    const bt = Date.parse(String(b));
    if (Number.isFinite(at) && Number.isFinite(bt)) return at - bt;
  }
  if (type === 'boolean' || typeof a === 'boolean' || typeof b === 'boolean') {
    return Number(a === true) - Number(b === true);
  }
  // Fallback: numerico se entrambi numerici (anche senza tipo dichiarato).
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const as = String(a);
  const bs = String(b);
  if (NUMERIC_STRING_RE.test(as.trim()) && NUMERIC_STRING_RE.test(bs.trim())) {
    return Number(as) - Number(bs);
  }
  return as.localeCompare(bs, 'it', { sensitivity: 'base', numeric: true });
}

function csvEscape(cell: ExportCell, delimiter: string): string {
  if (cell === null) return '';
  const s = typeof cell === 'string' ? cell : String(cell);
  if (s.includes('"') || s.includes(delimiter) || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** CSV (senza BOM: aggiunto dal download) — testabile come funzione pura. */
export function buildCsv(columns: ExportColumn[], rows: ExportRow[], delimiter = ';'): string {
  const header = columns.map(c => csvEscape(c.label, delimiter)).join(delimiter);
  const lines = rows.map(row =>
    columns.map(c => csvEscape(coerceCell(row[c.key], c.type), delimiter)).join(delimiter),
  );
  return [header, ...lines].join('\r\n');
}

/** Matrice header+righe per XLSX (aoa), con coercizione tipata delle celle. */
export function buildAoa(columns: ExportColumn[], rows: ExportRow[]): ExportCell[][] {
  return [
    columns.map(c => c.label),
    ...rows.map(row => columns.map(c => coerceCell(row[c.key], c.type))),
  ];
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Nome file sicuro a partire da slug/titolo artefatto. */
export function exportFilename(base: string, ext: string): string {
  const safe = base.trim().toLowerCase()
    .replace(/[^a-z0-9à-ÿ_-]+/gi, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '') || 'artefatto';
  return `${safe}.${ext}`;
}

export function exportTableCsv(columns: ExportColumn[], rows: ExportRow[], filenameBase: string): void {
  const csv = '\uFEFF' + buildCsv(columns, rows);
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), exportFilename(filenameBase, 'csv'));
}

/** XLSX reale via SheetJS, lazy-loaded solo al click di export. */
export async function exportTableXlsx(columns: ExportColumn[], rows: ExportRow[], filenameBase: string): Promise<void> {
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.aoa_to_sheet(buildAoa(columns, rows));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Dati');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  downloadBlob(
    new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    exportFilename(filenameBase, 'xlsx'),
  );
}
